import React, {useEffect, useRef, useState, useCallback} from "react";

type Shape = {
    id: string;
    type: "circle" | "polygon" | "line";
} & (
    | {type: "circle"; center: [number, number]; radiusMeters: number} // radius in meters
    | {type: "polygon" | "line"; vertices: Array<[number, number]>}
);

type DrawingTool = "circle" | "polygon" | "line" | "none";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAP_API_KEY || "";

export function DrawingTestPage() {
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const [shapes, setShapes] = useState<Shape[]>([]);
    const [activeTool, setActiveTool] = useState<DrawingTool>("none");
    const [selectedShapeId, setSelectedShapeId] = useState<string | null>(null);
    const [currentDrawing, setCurrentDrawing] = useState<Array<[number, number]>>([]);
    const [mousePosition, setMousePosition] = useState<[number, number] | null>(null);
    const [lineRadiusKm, setLineRadiusKm] = useState<number>(1); // Detection range in kilometers
    const [outlineWidth, setOutlineWidth] = useState<number>(1);
    const [draggedVertex, setDraggedVertex] = useState<{shapeId: string, vertexIndex: number} | null>(null);

    const mapDivRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const glRef = useRef<WebGLRenderingContext | null>(null);
    const programRef = useRef<WebGLProgram | null>(null);
    const postProcessProgramRef = useRef<WebGLProgram | null>(null);
    const framebufferRef = useRef<WebGLFramebuffer | null>(null);
    const maskTextureRef = useRef<WebGLTexture | null>(null);
    const fullscreenQuadBufferRef = useRef<WebGLBuffer | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const drawingListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const mouseMoveListenerRef = useRef<google.maps.MapsEventListener | null>(null);
    const currentDrawingRef = useRef<Array<[number, number]>>([]);
    const drawFuncRef = useRef<(() => void) | null>(null);
    const activeToolRef = useRef<DrawingTool>("none");
    const draggedVertexRef = useRef<{shapeId: string, vertexIndex: number} | null>(null);
    const shapesRef = useRef<Shape[]>([]);

    // Keep refs in sync with state
    useEffect(() => {
        currentDrawingRef.current = currentDrawing;
    }, [currentDrawing]);

    useEffect(() => {
        activeToolRef.current = activeTool;
    }, [activeTool]);

    useEffect(() => {
        draggedVertexRef.current = draggedVertex;
        console.log("draggedVertexRef updated to:", draggedVertex);
    }, [draggedVertex]);

    useEffect(() => {
        shapesRef.current = shapes;
    }, [shapes]);

    // Load Google Maps
    useEffect(() => {
        if ((window as any).google?.maps?.Map) {
            setTimeout(() => setGoogleLoaded(true), 100);
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&v=3.64`;
        script.async = true;
        script.onload = () => {
            setTimeout(() => {
                if ((window as any).google?.maps?.Map) {
                    setGoogleLoaded(true);
                }
            }, 200);
        };
        script.onerror = () => {
            console.error("Failed to load Google Maps");
        };
        document.head.appendChild(script);
    }, []);

    // Initialize WebGL
    const initWebGL = useCallback(() => {
        if (!canvasRef.current) return;

        const canvas = canvasRef.current;
        const gl = canvas.getContext("webgl", {
            alpha: true,
            premultipliedAlpha: false,
            antialias: true,
            stencil: false,
            depth: false
        });
        if (!gl) {
            console.error("WebGL not supported");
            return;
        }

        glRef.current = gl;

        // Enable blending for better antialiasing
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

        // Simple vertex shader
        const vs = `
            attribute vec2 a_position;
            attribute float a_size;
            attribute vec3 a_color;
            varying float v_size;
            varying vec3 v_color;
            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
                gl_PointSize = a_size;
                v_size = a_size;
                v_color = a_color;
            }
        `;

        // Simple fragment shader
        const fs = `
            precision highp float;
            varying float v_size;
            varying vec3 v_color;
            void main() {
                // Only apply circular masking for points (size > 1.0)
                if (v_size > 1.0) {
                    vec2 coord = gl_PointCoord - vec2(0.5);
                    float dist = length(coord) * 2.0;
                    if (dist > 1.0) discard;
                }

                gl_FragColor = vec4(v_color, 1.0);
            }
        `;

        const compileShader = (source: string, type: number) => {
            const shader = gl.createShader(type);
            if (!shader) return null;
            gl.shaderSource(shader, source);
            gl.compileShader(shader);
            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("Shader compile error:", gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        };

        const vertexShader = compileShader(vs, gl.VERTEX_SHADER);
        const fragmentShader = compileShader(fs, gl.FRAGMENT_SHADER);

        if (!vertexShader || !fragmentShader) return;

        const program = gl.createProgram();
        if (!program) return;
        gl.attachShader(program, vertexShader);
        gl.attachShader(program, fragmentShader);
        gl.linkProgram(program);

        if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
            console.error("Program link error:", gl.getProgramInfoLog(program));
            return;
        }

        programRef.current = program;

        // Create post-process shaders for stripe effect
        const postVs = `
            attribute vec2 a_position;
            varying vec2 v_texCoord;
            void main() {
                gl_Position = vec4(a_position, 0.0, 1.0);
                v_texCoord = a_position * 0.5 + 0.5;
            }
        `;

        const postFs = `
            precision highp float;
            varying vec2 v_texCoord;
            uniform sampler2D u_mask;
            uniform float u_time;
            uniform vec2 u_resolution;
            uniform float u_outlineWidth;

            // ============================================
            // STRIPE EFFECT CONFIGURATION
            // ============================================
            const float STRIPE_LINE_WIDTH = 1.0;      // Width of solid black lines (px)
            const float STRIPE_GAP_WIDTH = 4.0;       // Width of gaps between lines (px)
            const float STRIPE_ANGLE = 0.785398;      // Angle in radians (0.785398 = 45 degrees)
            const float SCROLL_SPEED = 20.0;          // Animation speed (px per second)

            // Background color for gaps (RGBA)
            const vec3 GAP_COLOR = vec3(0.2, 1.0, 0.2);     // RGB color (0,0,0 = black)
            const float GAP_ALPHA = 0.4;                    // Alpha (0.0 = transparent, 1.0 = opaque)

            // Line color (RGBA)
            const vec3 LINE_COLOR = vec3(0.0, 0.8, 0.0);    // RGB color (0,0,0 = black)
            const float LINE_ALPHA = 0.9;                   // Alpha (1.0 = opaque)

            // Outline color (RGBA)
            const vec3 OUTLINE_COLOR = vec3(0.0, 0.6, 0.0); // Black outline
            const float OUTLINE_ALPHA = 1.0;
            // ============================================

            void main() {
                vec4 mask = texture2D(u_mask, v_texCoord);
                bool isShape = mask.r < 0.1;

                // Check if we're near an edge for outline with antialiasing
                float outlineAlpha = 0.0;
                float minDistance = 999999.0;

                if (u_outlineWidth > 0.0 && !isShape) {
                    // Sample surrounding pixels in a circle pattern with more angles for smoothness
                    const int maxDist = 10; // Maximum outline width
                    const int numAngles = 32; // Increased from 16 for smoother edges

                    for (int angleIdx = 0; angleIdx < numAngles; angleIdx++) {
                        float angle = float(angleIdx) * 6.28318 / float(numAngles);

                        for (int distIdx = 1; distIdx <= maxDist; distIdx++) {
                            float dist = float(distIdx);
                            if (dist > u_outlineWidth + 1.0) break; // Sample slightly beyond outline width

                            // Sample in actual pixel offsets, then convert to texture space
                            // This ensures uniform sampling in all directions
                            vec2 pixelOffset = vec2(cos(angle), sin(angle)) * dist;
                            vec2 offset = pixelOffset / u_resolution;

                            // Use multiple sub-pixel samples for better antialiasing
                            float sampleSum = 0.0;
                            const float subPixelOffset = 0.5;
                            vec2 subPixelSize = vec2(subPixelOffset) / u_resolution;

                            vec4 s1 = texture2D(u_mask, v_texCoord + offset);
                            vec4 s2 = texture2D(u_mask, v_texCoord + offset + vec2(subPixelSize.x, 0.0));
                            vec4 s3 = texture2D(u_mask, v_texCoord + offset + vec2(0.0, subPixelSize.y));
                            vec4 s4 = texture2D(u_mask, v_texCoord + offset + subPixelSize);

                            sampleSum = (s1.r + s2.r + s3.r + s4.r) / 4.0;

                            if (sampleSum < 0.1) {
                                minDistance = min(minDistance, dist);
                                break;
                            }
                        }
                    }

                    // Calculate smooth alpha based on distance
                    if (minDistance < 999999.0) {
                        // Smooth transition at the edge of the outline
                        float edgeSoftness = 1.0; // Adjust for softer/harder edges
                        outlineAlpha = smoothstep(u_outlineWidth + edgeSoftness, u_outlineWidth - edgeSoftness, minDistance);
                        outlineAlpha = clamp(outlineAlpha, 0.0, 1.0);
                    }
                }

                // Render outline with antialiasing
                if (outlineAlpha > 0.0) {
                    gl_FragColor = vec4(OUTLINE_COLOR, OUTLINE_ALPHA * outlineAlpha);
                }
                // Render shape with stripe effect
                else if (isShape) {
                    // Convert to pixel coordinates
                    vec2 pixelPos = v_texCoord * u_resolution;

                    // Rotate pixel position by stripe angle
                    float s = sin(STRIPE_ANGLE);
                    float c = cos(STRIPE_ANGLE);
                    vec2 rotated = vec2(
                        pixelPos.x * c - pixelPos.y * s,
                        pixelPos.x * s + pixelPos.y * c
                    );

                    // Use vertical stripes (x-axis) after rotation
                    float pos = rotated.x + u_time * SCROLL_SPEED;

                    // Calculate stripe pattern
                    float totalWidth = STRIPE_LINE_WIDTH + STRIPE_GAP_WIDTH;
                    float stripe = mod(pos, totalWidth);

                    // Determine if we're in a line or gap (with anti-aliasing)
                    float edgeSmooth = 0.5;
                    float lineMask = 1.0 - smoothstep(STRIPE_LINE_WIDTH - edgeSmooth, STRIPE_LINE_WIDTH + edgeSmooth, stripe);

                    // Mix colors
                    vec3 color = mix(GAP_COLOR, LINE_COLOR, lineMask);
                    float alpha = mix(GAP_ALPHA, LINE_ALPHA, lineMask);

                    gl_FragColor = vec4(color, alpha);
                } else {
                    discard;
                }
            }
        `;

        const postVertexShader = compileShader(postVs, gl.VERTEX_SHADER);
        const postFragmentShader = compileShader(postFs, gl.FRAGMENT_SHADER);

        if (!postVertexShader || !postFragmentShader) return;

        const postProgram = gl.createProgram();
        if (!postProgram) return;
        gl.attachShader(postProgram, postVertexShader);
        gl.attachShader(postProgram, postFragmentShader);
        gl.linkProgram(postProgram);

        if (!gl.getProgramParameter(postProgram, gl.LINK_STATUS)) {
            console.error("Post-process program link error:", gl.getProgramInfoLog(postProgram));
            return;
        }

        postProcessProgramRef.current = postProgram;

        // Create fullscreen quad
        const quadVertices = new Float32Array([
            -1, -1,
             1, -1,
            -1,  1,
             1,  1
        ]);
        const quadBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, quadVertices, gl.STATIC_DRAW);
        fullscreenQuadBufferRef.current = quadBuffer;

        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    }, []);

    // Draw function
    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        const gl = glRef.current;
        const program = programRef.current;
        const postProgram = postProcessProgramRef.current;
        const map = mapRef.current;
        const overlay = overlayRef.current;

        if (!canvas || !gl || !program || !postProgram || !map || !overlay) return;

        try {
            const projection = (overlay as any).getProjectionSafe?.();
            if (!projection) return;

            const mapDiv = map.getDiv();
            if (!mapDiv) return;

            const dpr = window.devicePixelRatio || 1;
            const width = mapDiv.offsetWidth;
            const height = mapDiv.offsetHeight;

            if (!width || !height || width <= 0 || height <= 0) return;

            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            canvas.width = width * dpr;
            canvas.height = height * dpr;

            // Create or resize framebuffer and texture if needed
            if (!framebufferRef.current || !maskTextureRef.current) {
                const framebuffer = gl.createFramebuffer();
                const texture = gl.createTexture();

                if (!framebuffer || !texture) {
                    console.error("Failed to create framebuffer or texture");
                    return;
                }

                framebufferRef.current = framebuffer;
                maskTextureRef.current = texture;
            }

            // Setup mask texture
            gl.bindTexture(gl.TEXTURE_2D, maskTextureRef.current);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, canvas.width, canvas.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

            // Attach texture to framebuffer
            gl.bindFramebuffer(gl.FRAMEBUFFER, framebufferRef.current);
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, maskTextureRef.current, 0);

            // Check framebuffer status
            if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
                console.error("Framebuffer not complete");
                return;
            }

            // === PASS 1: Render completed shapes to mask texture ===
            gl.bindFramebuffer(gl.FRAMEBUFFER, framebufferRef.current);
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.clearColor(1, 1, 1, 1); // Clear to white (not part of mask)
            gl.clear(gl.COLOR_BUFFER_BIT);

            // Enable blending for smoother edges
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

            gl.useProgram(program);

            const posLoc = gl.getAttribLocation(program, "a_position");
            const sizeLoc = gl.getAttribLocation(program, "a_size");
            const colorLoc = gl.getAttribLocation(program, "a_color");

            // Helper to convert lat/lng to normalized device coordinates
            const latLngToNDC = (lng: number, lat: number): [number, number] | null => {
                const latLng = new google.maps.LatLng(lat, lng);
                const pixel = projection.fromLatLngToContainerPixel(latLng);
                if (!pixel || typeof pixel.x !== "number" || typeof pixel.y !== "number") return null;

                const normX = (pixel.x / width) * 2 - 1;
                const normY = -(pixel.y / height) * 2 + 1;
                return [normX, normY];
            };

            // Earth's radius in meters
            const EARTH_RADIUS = 6378137;

            // Helper to calculate distance between two lat/lng points in meters (Haversine formula)
            const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
                const toRad = (deg: number) => deg * Math.PI / 180;
                const dLat = toRad(lat2 - lat1);
                const dLng = toRad(lng2 - lng1);
                const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                         Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                         Math.sin(dLng / 2) * Math.sin(dLng / 2);
                const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                return EARTH_RADIUS * c;
            };

            // Helper to normalize longitude to -180 to 180 range
            const normalizeLng = (lng: number): number => {
                while (lng > 180) lng -= 360;
                while (lng < -180) lng += 360;
                return lng;
            };

            // Helper to calculate destination point given start point, bearing, and distance
            const destinationPoint = (lat: number, lng: number, bearing: number, distanceMeters: number): [number, number] => {
                const toRad = (deg: number) => deg * Math.PI / 180;
                const toDeg = (rad: number) => rad * 180 / Math.PI;

                const latRad = toRad(lat);
                const lngRad = toRad(lng);
                const bearingRad = toRad(bearing);

                const angularDistance = distanceMeters / EARTH_RADIUS;

                const lat2Rad = Math.asin(
                    Math.sin(latRad) * Math.cos(angularDistance) +
                    Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
                );

                const lng2Rad = lngRad + Math.atan2(
                    Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
                    Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(lat2Rad)
                );

                let lat2 = toDeg(lat2Rad);
                let lng2 = toDeg(lng2Rad);

                // Clamp latitude to valid range
                if (lat2 > 90) lat2 = 90;
                if (lat2 < -90) lat2 = -90;

                // Handle pole crossing - if we're at a pole, longitude is meaningless
                if (Math.abs(lat2) === 90) {
                    lng2 = lng; // Keep original longitude at poles
                }

                return [normalizeLng(lng2), lat2];
            };

            // Helper to convert meters to pixels at a given latitude
            const metersToPixels = (meters: number, lat: number): number => {
                // Calculate meters per pixel at this latitude
                const zoom = map.getZoom() || 0;
                const scale = (1 << zoom) * 256; // Total pixels at this zoom level
                const metersPerPixel = (2 * Math.PI * EARTH_RADIUS * Math.cos(lat * Math.PI / 180)) / scale;

                return meters / metersPerPixel;
            };

            // Draw completed shapes
            for (const shape of shapes) {
                if (shape.type === "circle") {
                    // Draw circle as geographic circle (proper great circle)
                    const centerNDC = latLngToNDC(shape.center[0], shape.center[1]);
                    if (!centerNDC) continue;

                    // Draw circle as triangle fan with geographic accuracy
                    const segments = 64; // Number of segments for smooth circle

                    // Generate all circle edge points
                    const edgePoints: Array<{lng: number, lat: number, ndc: [number, number] | null}> = [];
                    for (let i = 0; i <= segments; i++) {
                        const bearing = (i / segments) * 360;
                        const [edgeLng, edgeLat] = destinationPoint(
                            shape.center[1],
                            shape.center[0],
                            bearing,
                            shape.radiusMeters
                        );
                        edgePoints.push({
                            lng: edgeLng,
                            lat: edgeLat,
                            ndc: latLngToNDC(edgeLng, edgeLat)
                        });
                    }

                    // Check if circle crosses antimeridian (date line)
                    let crossesAntimeridian = false;
                    for (let i = 0; i < edgePoints.length - 1; i++) {
                        const lngDiff = Math.abs(edgePoints[i + 1].lng - edgePoints[i].lng);
                        if (lngDiff > 180) {
                            crossesAntimeridian = true;
                            break;
                        }
                    }

                    if (crossesAntimeridian) {
                        // Draw circle multiple times with longitude offsets to handle wrapping
                        // This renders the circle at its original position and at +/- 360 degrees
                        const offsets = [-360, 0, 360];

                        for (const offset of offsets) {
                            const adjustedCenterLng = shape.center[0] + offset;
                            const adjustedCenterNDC = latLngToNDC(adjustedCenterLng, shape.center[1]);
                            if (!adjustedCenterNDC) continue;

                            const positions: number[] = [];
                            positions.push(adjustedCenterNDC[0], adjustedCenterNDC[1]);

                            let hasVisibleVertices = false;
                            for (let i = 0; i <= segments; i++) {
                                const bearing = (i / segments) * 360;
                                const [edgeLng, edgeLat] = destinationPoint(
                                    shape.center[1],
                                    shape.center[0],
                                    bearing,
                                    shape.radiusMeters
                                );

                                const adjustedEdgeLng = edgeLng + offset;
                                const ndc = latLngToNDC(adjustedEdgeLng, edgeLat);
                                if (ndc) {
                                    positions.push(ndc[0], ndc[1]);
                                    hasVisibleVertices = true;
                                }
                            }

                            // Only draw if we have vertices in view
                            if (!hasVisibleVertices || positions.length < 6) continue;

                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(new Array(positions.length / 2).fill(0)), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            const colors: number[] = [];
                            for (let i = 0; i < positions.length / 2; i++) {
                                colors.push(0.0, 0.0, 0.0);
                            }
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
                        }
                    } else {
                        // Normal rendering for circles that don't cross antimeridian
                        const positions: number[] = [];
                        positions.push(centerNDC[0], centerNDC[1]);

                        for (const point of edgePoints) {
                            if (point.ndc) {
                                positions.push(point.ndc[0], point.ndc[1]);
                            }
                        }

                        const posBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(posLoc);
                        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                        const sizeBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(new Array(positions.length / 2).fill(0)), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(sizeLoc);
                        gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                        const colorBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                        const colors: number[] = [];
                        for (let i = 0; i < positions.length / 2; i++) {
                            colors.push(0.0, 0.0, 0.0);
                        }
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(colorLoc);
                        gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                        gl.drawArrays(gl.TRIANGLE_FAN, 0, positions.length / 2);
                    }
                } else {
                    // Draw polygon/line with rounded vertices
                    const positions: number[] = [];
                    const vertexPixelPositions: Array<{x: number, y: number}> = [];

                    for (const [lng, lat] of shape.vertices) {
                        const ndc = latLngToNDC(lng, lat);
                        if (!ndc) continue;
                        positions.push(ndc[0], ndc[1]);

                        // Store pixel position for line width calculation
                        const latLng = new google.maps.LatLng(lat, lng);
                        const pixel = projection.fromLatLngToContainerPixel(latLng);
                        if (pixel) {
                            vertexPixelPositions.push({x: pixel.x, y: pixel.y});
                        }
                    }

                    if (positions.length > 0) {
                        const vertexCount = positions.length / 2;

                        if (shape.type === "polygon" && positions.length >= 6) {
                            // Polygons: only render filled shape (no thick border)
                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizes = new Array(vertexCount).fill(1.0);
                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(sizes), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            const colors: number[] = [];
                            for (let i = 0; i < vertexCount; i++) {
                                colors.push(0.0, 0.0, 0.0);
                            }
                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.TRIANGLE_FAN, 0, vertexCount);
                        } else if (shape.type === "line") {
                            // Lines: render thick line with quads + circles
                            // Calculate line radius in pixels based on the first vertex's latitude
                            const firstVertex = shape.vertices[0];
                            const lineRadiusMeters = lineRadiusKm * 1000; // Convert km to meters
                            const lineRadiusPixels = Math.max(2, metersToPixels(lineRadiusMeters, firstVertex[1])); // Minimum 2px radius for visibility

                        // Densify line segments to ensure continuous rendering
                        const densifiedPixelPositions: Array<{x: number, y: number}> = [];
                        for (let i = 0; i < vertexPixelPositions.length; i++) {
                            densifiedPixelPositions.push(vertexPixelPositions[i]);

                            if (i < vertexPixelPositions.length - 1) {
                                const p1 = vertexPixelPositions[i];
                                const p2 = vertexPixelPositions[i + 1];
                                const dx = p2.x - p1.x;
                                const dy = p2.y - p1.y;
                                const segmentLength = Math.sqrt(dx * dx + dy * dy);

                                // Add intermediate points if segment is longer than 2x the line radius
                                const maxSegmentLength = Math.max(lineRadiusPixels * 2, 10);
                                if (segmentLength > maxSegmentLength) {
                                    const numIntermediatePoints = Math.ceil(segmentLength / maxSegmentLength) - 1;
                                    for (let j = 1; j <= numIntermediatePoints; j++) {
                                        const t = j / (numIntermediatePoints + 1);
                                        densifiedPixelPositions.push({
                                            x: p1.x + dx * t,
                                            y: p1.y + dy * t
                                        });
                                    }
                                }
                            }
                        }

                        for (let i = 0; i < densifiedPixelPositions.length - 1; i++) {
                            const p1 = densifiedPixelPositions[i];
                            const p2 = densifiedPixelPositions[i + 1];

                            // Calculate direction and perpendicular
                            const dx = p2.x - p1.x;
                            const dy = p2.y - p1.y;
                            const len = Math.sqrt(dx * dx + dy * dy);

                            if (len > 0) {
                                // Perpendicular vector (normalized)
                                const perpX = (-dy / len) * lineRadiusPixels;
                                const perpY = (dx / len) * lineRadiusPixels;

                                // Create 4 corners of the quad in pixel space
                                const p1x1 = p1.x + perpX;
                                const p1y1 = p1.y + perpY;
                                const p1x2 = p1.x - perpX;
                                const p1y2 = p1.y - perpY;
                                const p2x1 = p2.x + perpX;
                                const p2y1 = p2.y + perpY;
                                const p2x2 = p2.x - perpX;
                                const p2y2 = p2.y - perpY;

                                // Convert to NDC
                                const toNDC = (px: number, py: number): [number, number] => {
                                    return [(px / width) * 2 - 1, -((py / height) * 2 - 1)];
                                };

                                const [n1x1, n1y1] = toNDC(p1x1, p1y1);
                                const [n1x2, n1y2] = toNDC(p1x2, p1y2);
                                const [n2x1, n2y1] = toNDC(p2x1, p2y1);
                                const [n2x2, n2y2] = toNDC(p2x2, p2y2);

                                // Create quad
                                const quadPositions = new Float32Array([
                                    n1x1, n1y1,
                                    n1x2, n1y2,
                                    n2x1, n2y1,
                                    n2x2, n2y2
                                ]);

                                const posBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, quadPositions, gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(posLoc);
                                gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                                const sizes = new Array(4).fill(1.0);
                                const sizeBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(sizes), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(sizeLoc);
                                gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                                const colors: number[] = [0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0, 0.0];
                                const colorBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(colorLoc);
                                gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                                gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
                            }
                        }

                        // Draw circles at each densified point to ensure continuous line
                        // The diameter should match the quad width (lineRadiusPixels is the radius, so diameter = lineRadiusPixels * 2)
                        const circleDiameter = lineRadiusPixels * 2 * dpr;

                        for (let i = 0; i < densifiedPixelPositions.length; i++) {
                            const pixelPos = densifiedPixelPositions[i];
                            const ndcX = (pixelPos.x / width) * 2 - 1;
                            const ndcY = -((pixelPos.y / height) * 2 - 1);

                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([ndcX, ndcY]), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([circleDiameter]), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0.0, 0.0, 0.0]), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.POINTS, 0, 1);
                        }
                        }
                    }
                }
            }

            // === PASS 2: Render mask with stripe effect to screen ===
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            gl.viewport(0, 0, canvas.width, canvas.height);
            gl.clearColor(0, 0, 0, 0);
            gl.clear(gl.COLOR_BUFFER_BIT);

            // Ensure blending is enabled for transparent rendering
            gl.enable(gl.BLEND);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

            gl.useProgram(postProgram);

            // Bind the mask texture
            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, maskTextureRef.current);

            const postPosLoc = gl.getAttribLocation(postProgram, "a_position");
            const maskLoc = gl.getUniformLocation(postProgram, "u_mask");
            const timeLoc = gl.getUniformLocation(postProgram, "u_time");
            const resolutionLoc = gl.getUniformLocation(postProgram, "u_resolution");
            const outlineWidthLoc = gl.getUniformLocation(postProgram, "u_outlineWidth");

            gl.uniform1i(maskLoc, 0);
            gl.uniform1f(timeLoc, performance.now() / 1000.0);
            gl.uniform2f(resolutionLoc, canvas.width, canvas.height);
            gl.uniform1f(outlineWidthLoc, outlineWidth);

            // Draw fullscreen quad
            gl.bindBuffer(gl.ARRAY_BUFFER, fullscreenQuadBufferRef.current);
            gl.enableVertexAttribArray(postPosLoc);
            gl.vertexAttribPointer(postPosLoc, 2, gl.FLOAT, false, 0, 0);
            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

            // === PASS 3: Render preview shapes on top ===
            gl.useProgram(program);

            // Draw preview
            if (currentDrawing.length > 0) {
                if (activeToolRef.current === "circle" && currentDrawing.length === 2) {
                    // Preview circle
                    const center = currentDrawing[0];
                    const radiusPoint = currentDrawing[1];

                    const centerLatLng = new google.maps.LatLng(center[1], center[0]);
                    const centerPixel = projection.fromLatLngToContainerPixel(centerLatLng);
                    if (centerPixel) {
                        const dx = radiusPoint[0] - center[0];
                        const dy = radiusPoint[1] - center[1];
                        const latCos = Math.cos((center[1] * Math.PI) / 180);
                        const adjustedDx = dx * latCos;
                        const radius = Math.sqrt(adjustedDx * adjustedDx + dy * dy);

                        const radiusLat = center[1] + radius;
                        const radiusLatLng = new google.maps.LatLng(radiusLat, center[0]);
                        const radiusPixel = projection.fromLatLngToContainerPixel(radiusLatLng);

                        if (radiusPixel) {
                            const radiusInPixels = Math.abs(radiusPixel.y - centerPixel.y);
                            const centerNDC = latLngToNDC(center[0], center[1]);

                            if (centerNDC) {
                                const posBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(centerNDC), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(posLoc);
                                gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                                const sizeBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([radiusInPixels * 2]), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(sizeLoc);
                                gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                                const colorBuffer = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1.0, 0.0, 0.0]), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(colorLoc);
                                gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                                gl.drawArrays(gl.POINTS, 0, 1);
                            }
                        }
                    }
                } else {
                    // Preview line/polygon
                    const positions: number[] = [];
                    for (const [lng, lat] of currentDrawing) {
                        const ndc = latLngToNDC(lng, lat);
                        if (!ndc) continue;
                        positions.push(ndc[0], ndc[1]);
                    }

                    // Add mouse position to preview if available
                    if (mousePosition && currentDrawing.length > 0) {
                        const mouseNDC = latLngToNDC(mousePosition[0], mousePosition[1]);
                        if (mouseNDC) {
                            positions.push(mouseNDC[0], mouseNDC[1]);
                        }
                    }

                    if (positions.length > 0) {
                        const vertexCount = positions.length / 2;

                        // For polygons with 3+ points, draw filled preview
                        if (activeToolRef.current === "polygon" && vertexCount >= 3) {
                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizes = new Array(vertexCount).fill(1.0);
                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(sizes), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            // Semi-transparent red for filled preview
                            const colors: number[] = [];
                            for (let i = 0; i < vertexCount; i++) {
                                colors.push(1.0, 0.0, 0.0); // Red
                            }
                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.TRIANGLE_FAN, 0, vertexCount);
                        }

                        // Always draw the outline
                        const posBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(positions), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(posLoc);
                        gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                        const sizes = new Array(vertexCount).fill(1.0);
                        const sizeBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(sizes), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(sizeLoc);
                        gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                        // Red color for preview outline
                        const colors: number[] = [];
                        for (let i = 0; i < vertexCount; i++) {
                            colors.push(1.0, 0.0, 0.0); // Red
                        }
                        const colorBuffer = gl.createBuffer();
                        gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(colors), gl.STATIC_DRAW);
                        gl.enableVertexAttribArray(colorLoc);
                        gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                        gl.drawArrays(gl.LINE_STRIP, 0, vertexCount);
                    }
                }
            }

            // === PASS 4: Render edit handles for selected shapes ===
            if (selectedShapeId && activeTool === "none") {
                const selectedShape = shapes.find(s => s.id === selectedShapeId);
                if (selectedShape) {
                    console.log("Rendering handles for shape:", selectedShape.id, "type:", selectedShape.type);
                    const handleSize = 12 * dpr; // 12px handles

                    if (selectedShape.type === "circle") {
                        // Draw handle at center and edge
                        const centerNDC = latLngToNDC(selectedShape.center[0], selectedShape.center[1]);
                        if (centerNDC) {
                            // Center handle
                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(centerNDC), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([handleSize]), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1.0, 1.0, 1.0]), gl.STATIC_DRAW); // White
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.POINTS, 0, 1);

                            // Edge handle (radius control) - place it to the east (90 degrees bearing)
                            // Use geographic calculation for accurate positioning
                            const [edgeLng, edgeLat] = destinationPoint(
                                selectedShape.center[1],
                                selectedShape.center[0],
                                90, // East bearing
                                selectedShape.radiusMeters
                            );
                            const edgeNDC = latLngToNDC(edgeLng, edgeLat);
                            if (edgeNDC) {
                                const posBuffer2 = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer2);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(edgeNDC), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(posLoc);
                                gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                                const sizeBuffer2 = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer2);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([handleSize]), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(sizeLoc);
                                gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                                const colorBuffer2 = gl.createBuffer();
                                gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer2);
                                gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1.0, 1.0, 1.0]), gl.STATIC_DRAW);
                                gl.enableVertexAttribArray(colorLoc);
                                gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                                gl.drawArrays(gl.POINTS, 0, 1);
                            }
                        }
                    } else {
                        // Draw handles at each vertex for polygons and lines
                        for (let i = 0; i < selectedShape.vertices.length; i++) {
                            const vertex = selectedShape.vertices[i];
                            const vertexNDC = latLngToNDC(vertex[0], vertex[1]);
                            if (!vertexNDC) continue;

                            const posBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, posBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertexNDC), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(posLoc);
                            gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

                            const sizeBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, sizeBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([handleSize]), gl.STATIC_DRAW);
                            gl.enableVertexAttribArray(sizeLoc);
                            gl.vertexAttribPointer(sizeLoc, 1, gl.FLOAT, false, 0, 0);

                            const colorBuffer = gl.createBuffer();
                            gl.bindBuffer(gl.ARRAY_BUFFER, colorBuffer);
                            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([1.0, 1.0, 1.0]), gl.STATIC_DRAW); // White handles
                            gl.enableVertexAttribArray(colorLoc);
                            gl.vertexAttribPointer(colorLoc, 3, gl.FLOAT, false, 0, 0);

                            gl.drawArrays(gl.POINTS, 0, 1);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Draw error:", error);
        }
    }, [shapes, currentDrawing, mousePosition, activeTool, lineRadiusKm, outlineWidth, selectedShapeId]);

    // Keep draw function ref in sync
    useEffect(() => {
        drawFuncRef.current = draw;
    }, [draw]);

    // Init map and canvas
    useEffect(() => {
        if (!googleLoaded || !mapDivRef.current) return;

        const center = {lat: 0, lng: 0};

        const map = new google.maps.Map(mapDivRef.current, {
            center,
            zoom: 3,
            gestureHandling: "greedy",
            mapTypeControl: true,
        });

        mapRef.current = map;

        // Create canvas overlay
        const canvas = document.createElement("canvas");
        canvas.style.position = "absolute";
        canvas.style.left = "0";
        canvas.style.top = "0";
        canvas.style.pointerEvents = "none";
        canvasRef.current = canvas;

        const mapDiv = map.getDiv();
        mapDiv.appendChild(canvas);

        // Create OverlayView for projection
        class CanvasOverlay extends google.maps.OverlayView {
            private isReady = false;

            onAdd() {
                this.isReady = true;
            }

            draw() {
                if (drawFuncRef.current) {
                    drawFuncRef.current();
                }
            }

            onRemove() {
                this.isReady = false;
            }

            getProjectionSafe() {
                return this.isReady ? this.getProjection() : null;
            }
        }

        initWebGL();

        const overlay = new CanvasOverlay();
        overlay.setMap(map);
        overlayRef.current = overlay;

        setTimeout(() => {
            if (drawFuncRef.current) {
                drawFuncRef.current();
            }
        }, 100);

        return () => {
            overlay.setMap(null);
            if (canvasRef.current) {
                canvasRef.current.remove();
            }
        };
    }, [googleLoaded, initWebGL]);

    // Redraw when shapes or current drawing change
    useEffect(() => {
        if (overlayRef.current && mapRef.current && glRef.current && programRef.current) {
            draw();
        }
    }, [shapes, currentDrawing, draw]);

    // Animation loop for scrolling stripe effect
    useEffect(() => {
        let animationFrameId: number;

        const animate = () => {
            if (overlayRef.current && mapRef.current && glRef.current && programRef.current) {
                draw();
            }
            animationFrameId = requestAnimationFrame(animate);
        };

        // Only start animation loop if there are completed shapes
        if (shapes.length > 0) {
            animationFrameId = requestAnimationFrame(animate);
        }

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [shapes, draw]);

    // Complete a shape
    const completeShape = useCallback((shapeData: Shape) => {
        setShapes(prev => [...prev, shapeData]);
        setActiveTool("none");
        setCurrentDrawing([]);
        setMousePosition(null);
    }, []);

    // Handle drawing interactions
    useEffect(() => {
        const map = mapRef.current;
        const canvas = canvasRef.current;
        const overlay = overlayRef.current;

        if (!map || !canvas || !overlay) {
            return;
        }

        // When drawing or no shape selected → canvas must be transparent to allow map interactions
        if (activeTool !== "none" || !selectedShapeId) {
            console.log("Setting canvas pointerEvents to none. activeTool:", activeTool, "selectedShapeId:", selectedShapeId);
            canvas.style.pointerEvents = "none";
            return;
        }

        // Only enable interaction when editing a selected shape (and not drawing)
        console.log("Setting canvas pointerEvents to auto for handle editing");
        canvas.style.pointerEvents = "auto";
        canvas.style.cursor = "default";

        let dragged: { shapeId: string; vertexIndex: number } | null = null;
        const handleRadius = 14;

        const getHandleUnderPoint = (clientX: number, clientY: number) => {
            const rect = canvas.getBoundingClientRect();
            const x = clientX - rect.left;
            const y = clientY - rect.top;
            const proj = (overlay as any).getProjectionSafe?.();
            if (!proj) return null;

            const shape = shapesRef.current.find(s => s.id === selectedShapeId);
            if (!shape) return null;

            if (shape.type === "circle") {
                const centerPx = proj.fromLatLngToContainerPixel(new google.maps.LatLng(shape.center[1], shape.center[0]));
                if (centerPx && Math.hypot(centerPx.x - x, centerPx.y - y) < handleRadius)
                    return { shapeId: shape.id, vertexIndex: 0 };

                // Edge handle is positioned to the east (90 degrees bearing)
                // Need access to destinationPoint - this requires the map context
                // For now, approximate using simple offset (will update in drag handler)
                const tempEdgeLng = shape.center[0] + (shape.radiusMeters / 111320); // rough approximation
                const edgePx = proj.fromLatLngToContainerPixel(new google.maps.LatLng(shape.center[1], tempEdgeLng));
                if (edgePx && Math.hypot(edgePx.x - x, edgePx.y - y) < handleRadius)
                    return { shapeId: shape.id, vertexIndex: 1 };
            } else {
                for (let i = 0; i < shape.vertices.length; i++) {
                    const v = shape.vertices[i];
                    const px = proj.fromLatLngToContainerPixel(new google.maps.LatLng(v[1], v[0]));
                    if (px && Math.hypot(px.x - x, px.y - y) < handleRadius)
                        return { shapeId: shape.id, vertexIndex: i };
                }
            }
            return null;
        };

        const onMouseDown = (e: MouseEvent) => {
            if (e.button !== 0) return;
            const handle = getHandleUnderPoint(e.clientX, e.clientY);
            if (!handle) {
                setSelectedShapeId(null);   // click outside = deselect
                return;
            }
            dragged = handle;
            setDraggedVertex(handle);
            draggedVertexRef.current = handle;
            map.setOptions({ draggable: false });
            canvas.style.cursor = "grabbing";
            e.preventDefault();
            e.stopPropagation();
        };

        const onMouseMove = (e: MouseEvent) => {
            if (!dragged) {
                const handle = getHandleUnderPoint(e.clientX, e.clientY);
                canvas.style.cursor = handle ? "grab" : "default";
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const point = new google.maps.Point(e.clientX - rect.left, e.clientY - rect.top);
            const latLng = (overlay as any).getProjectionSafe?.().fromContainerPixelToLatLng(point);
            if (!latLng || !dragged) return;

            const lng = latLng.lng();
            const lat = latLng.lat();
            const draggedShapeId = dragged.shapeId;
            const draggedVertexIndex = dragged.vertexIndex;

            setShapes(prev => prev.map(s => {
                if (s.id !== draggedShapeId) return s;
                if (s.type === "circle") {
                    if (draggedVertexIndex === 0) {
                        return { ...s, center: [lng, lat] };
                    } else {
                        // Calculate radius in meters using Haversine formula
                        const EARTH_RADIUS = 6378137;
                        const toRad = (deg: number) => deg * Math.PI / 180;
                        const lat1 = s.center[1];
                        const lng1 = s.center[0];
                        const lat2 = lat;
                        const lng2 = lng;
                        const dLat = toRad(lat2 - lat1);
                        const dLng = toRad(lng2 - lng1);
                        const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                                 Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                                 Math.sin(dLng / 2) * Math.sin(dLng / 2);
                        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                        const radiusMeters = EARTH_RADIUS * c;
                        return { ...s, radiusMeters };
                    }
                } else {
                    const verts = [...s.vertices];
                    verts[draggedVertexIndex] = [lng, lat];
                    return { ...s, vertices: verts };
                }
            }));
        };

        const onMouseUp = () => {
            if (dragged) {
                dragged = null;
                setDraggedVertex(null);
                draggedVertexRef.current = null;
                map.setOptions({ draggable: true });
                canvas.style.cursor = "default";
            }
        };

        canvas.addEventListener("mousedown", onMouseDown);
        canvas.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);

        return () => {
            canvas.style.pointerEvents = "none";   // ← crucial cleanup
            canvas.style.cursor = "default";
            canvas.removeEventListener("mousedown", onMouseDown);
            canvas.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            map.setOptions({ draggable: true });
        };
    }, [activeTool, selectedShapeId]);

    // Setup drawing tool listeners
    useEffect(() => {
        const map = mapRef.current;
        const overlay = overlayRef.current;

        if (!map || !overlay || activeTool === "none") {
            // Remove previous listeners
            if (drawingListenerRef.current) {
                google.maps.event.removeListener(drawingListenerRef.current);
                drawingListenerRef.current = null;
            }
            if (mouseMoveListenerRef.current) {
                google.maps.event.removeListener(mouseMoveListenerRef.current);
                mouseMoveListenerRef.current = null;
            }
            setCurrentDrawing([]);
            setMousePosition(null);
            return;
        }

        // Remove previous listeners
        if (drawingListenerRef.current) {
            google.maps.event.removeListener(drawingListenerRef.current);
            drawingListenerRef.current = null;
        }
        if (mouseMoveListenerRef.current) {
            google.maps.event.removeListener(mouseMoveListenerRef.current);
            mouseMoveListenerRef.current = null;
        }

        if (activeTool === "circle") {
            // Circle: first click sets center, mousemove shows preview, second click sets radius
            drawingListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const newPoint: [number, number] = [lng, lat];

                const currentPoints = currentDrawingRef.current;

                if (currentPoints.length === 0) {
                    setCurrentDrawing([newPoint]);
                } else {
                    const center = currentPoints[0];
                    // Calculate radius in meters using Haversine formula
                    const EARTH_RADIUS = 6378137;
                    const toRad = (deg: number) => deg * Math.PI / 180;
                    const lat1 = center[1];
                    const lng1 = center[0];
                    const lat2 = newPoint[1];
                    const lng2 = newPoint[0];
                    const dLat = toRad(lat2 - lat1);
                    const dLng = toRad(lng2 - lng1);
                    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                             Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
                             Math.sin(dLng / 2) * Math.sin(dLng / 2);
                    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    const radiusMeters = EARTH_RADIUS * c;

                    completeShape({
                        id: Math.random().toString(36).substr(2, 9),
                        type: "circle",
                        center,
                        radiusMeters
                    });
                }
            });

            mouseMoveListenerRef.current = map.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
                const currentPoints = currentDrawingRef.current;
                if (!e.latLng) return;

                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const newPoint: [number, number] = [lng, lat];

                setMousePosition([lng, lat]);
                if (currentPoints.length > 0) {
                    setCurrentDrawing([currentPoints[0], newPoint]);
                }
            });
        } else {
            // Polygon/Line: click to add points
            drawingListenerRef.current = map.addListener("click", (e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;

                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                const newPoint: [number, number] = [lng, lat];

                const currentPoints = currentDrawingRef.current;

                // Check if polygon and clicking near first point (auto-close)
                if (activeToolRef.current === "polygon" && currentPoints.length >= 3) {
                    const firstPoint = currentPoints[0];
                    const firstLatLng = new google.maps.LatLng(firstPoint[1], firstPoint[0]);
                    const clickLatLng = new google.maps.LatLng(lat, lng);

                    const projection = (overlayRef.current as any)?.getProjectionSafe?.();
                    if (projection) {
                        const firstPixel = projection.fromLatLngToContainerPixel(firstLatLng);
                        const clickPixel = projection.fromLatLngToContainerPixel(clickLatLng);

                        if (firstPixel && clickPixel) {
                            const dx = clickPixel.x - firstPixel.x;
                            const dy = clickPixel.y - firstPixel.y;
                            const distanceInPixels = Math.sqrt(dx * dx + dy * dy);

                            if (distanceInPixels < 20) {
                                completeShape({
                                    id: Math.random().toString(36).substr(2, 9),
                                    type: "polygon",
                                    vertices: currentPoints
                                });
                                return;
                            }
                        }
                    }
                }

                setCurrentDrawing([...currentPoints, newPoint]);
            });

            mouseMoveListenerRef.current = map.addListener("mousemove", (e: google.maps.MapMouseEvent) => {
                if (!e.latLng) return;

                const lat = e.latLng.lat();
                const lng = e.latLng.lng();
                setMousePosition([lng, lat]);
            });
        }

        return () => {
            if (drawingListenerRef.current) {
                google.maps.event.removeListener(drawingListenerRef.current);
                drawingListenerRef.current = null;
            }
            if (mouseMoveListenerRef.current) {
                google.maps.event.removeListener(mouseMoveListenerRef.current);
                mouseMoveListenerRef.current = null;
            }
        };
    }, [activeTool, completeShape]);

    // Handle keyboard events
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                setCurrentDrawing([]);
                setActiveTool("none");
            } else if (e.key === "Enter" && currentDrawing.length > 0) {
                if (activeTool === "line" && currentDrawing.length >= 2) {
                    completeShape({
                        id: Math.random().toString(36).substr(2, 9),
                        type: "line",
                        vertices: currentDrawing
                    });
                    setCurrentDrawing([]);
                } else if (activeTool === "polygon" && currentDrawing.length >= 3) {
                    completeShape({
                        id: Math.random().toString(36).substr(2, 9),
                        type: "polygon",
                        vertices: currentDrawing
                    });
                    setCurrentDrawing([]);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentDrawing, activeTool, completeShape]);

    const startDrawing = (type: DrawingTool) => {
        setActiveTool(type);
        setCurrentDrawing([]);
        setSelectedShapeId(null);
    };

    const deleteShape = (id: string) => {
        setShapes(prev => prev.filter(s => s.id !== id));
    };

    const getShapeDescription = (shape: Shape): string => {
        if (shape.type === "circle") {
            const radiusKm = (shape.radiusMeters / 1000).toFixed(2);
            return `Center: [${shape.center[0].toFixed(4)}, ${shape.center[1].toFixed(4)}], Radius: ${radiusKm}km`;
        }
        return `${shape.vertices.length} vertices`;
    };

    return (
        <div className="h-screen flex flex-col pt-36">
            <div className="bg-white border-b border-light-grey p-4">
                <h1 className="text-2xl font-bold">WebGL Drawing Test (Clean)</h1>
            </div>

            <div className="flex-1 flex overflow-hidden">
                <div className="w-80 bg-white border-r border-light-grey flex flex-col">
                    <div className="p-4 border-b border-light-grey">
                        <h2 className="font-semibold mb-3">Drawing Tools</h2>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => startDrawing("circle")}
                                className={`px-4 py-2 text-white rounded hover:bg-dark-blue ${
                                    activeTool === "circle" ? "bg-dark-blue" : "bg-blue"
                                }`}
                            >
                                Circle
                            </button>
                            <button
                                onClick={() => startDrawing("polygon")}
                                className={`px-4 py-2 text-white rounded hover:bg-dark-blue ${
                                    activeTool === "polygon" ? "bg-dark-blue" : "bg-blue"
                                }`}
                            >
                                Polygon
                            </button>
                            <button
                                onClick={() => startDrawing("line")}
                                className={`px-4 py-2 text-white rounded hover:bg-dark-blue ${
                                    activeTool === "line" ? "bg-dark-blue" : "bg-blue"
                                }`}
                            >
                                Line
                            </button>
                            <button
                                onClick={() => setShapes([])}
                                className="px-4 py-2 bg-red text-white rounded hover:bg-dark-red"
                            >
                                Clear All
                            </button>
                        </div>
                        {activeTool !== "none" && (
                            <div className="mt-3 text-sm text-grey">
                                {activeTool === "circle" && "Click center, then click to set radius"}
                                {activeTool === "line" && "Click points, press Enter to finish"}
                                {activeTool === "polygon" && "Click points, press Enter to finish (or click near start to close)"}
                                <div className="mt-1 text-xs">Press Escape to cancel</div>
                            </div>
                        )}

                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-2">
                                Detection Range: {lineRadiusKm}km
                            </label>
                            <input
                                type="range"
                                min="0.1"
                                max="100"
                                step="0.1"
                                value={lineRadiusKm}
                                onChange={(e) => setLineRadiusKm(parseFloat(e.target.value))}
                                className="w-full"
                            />
                        </div>

                        <div className="mt-4">
                            <label className="block text-sm font-medium mb-2">
                                Outline Width: {outlineWidth}px
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="10"
                                step="0.5"
                                value={outlineWidth}
                                onChange={(e) => setOutlineWidth(parseFloat(e.target.value))}
                                className="w-full"
                            />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4">
                        <h2 className="font-semibold mb-3">Shapes ({shapes.length})</h2>
                        <div className="space-y-2">
                            {shapes.map(shape => (
                                <div
                                    key={shape.id}
                                    className={`p-3 border rounded cursor-pointer hover:bg-desaturated-grey ${
                                        selectedShapeId === shape.id ? "border-blue bg-light-blue" : "border-light-grey"
                                    }`}
                                    onClick={() => setSelectedShapeId(shape.id)}
                                >
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <div className="font-medium capitalize">{shape.type}</div>
                                            <div className="text-xs text-grey">
                                                {getShapeDescription(shape)}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                deleteShape(shape.id);
                                            }}
                                            className="text-red hover:text-dark-red"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex-1 relative">
                    <div ref={mapDivRef} className="w-full h-full" />
                </div>
            </div>
        </div>
    );
}
